import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import './SystemNotifications.scss';

type NotificationType = 'info' | 'warning' | 'error' | 'success';

interface SysNotification {
  id: string;
  message: string;
  type: NotificationType;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const TYPE_OPTIONS: NotificationType[] = ['info', 'warning', 'error', 'success'];

const SystemNotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<SysNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<NotificationType>('info');
  const [activate, setActivate] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      const res = await api.get<{ notifications: SysNotification[] }>('/system-notification/all');
      setNotifications(res.data.notifications);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/system-notification', { message, type, activate });
      toast.success('Notification created');
      setMessage('');
      fetchAll();
    } catch {
      toast.error('Failed to create notification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await api.patch(`/system-notification/${id}/activate`);
      toast.success('Notification activated');
      fetchAll();
    } catch {
      toast.error('Failed to activate');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await api.patch(`/system-notification/${id}/deactivate`);
      toast.success('Notification deactivated');
      fetchAll();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this notification?')) return;
    try {
      await api.delete(`/system-notification/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleString();

  return (
    <div className="sysnotif-page">
      <div className="sysnotif-header">
        <h1>System Notifications</h1>
        <p>Manage the banner shown to all users on page load. Only one can be active at a time.</p>
      </div>

      {/* Create Form */}
      <section className="sysnotif-create">
        <h2>Create Notification</h2>
        <form onSubmit={handleCreate} className="sysnotif-form">
          <textarea
            className="sysnotif-textarea"
            placeholder="Notification message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            required
          />
          <div className="sysnotif-form-row">
            <label className="sysnotif-label">
              Type
              <select
                className="sysnotif-select"
                value={type}
                onChange={(e) => setType(e.target.value as NotificationType)}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="sysnotif-checkbox-label">
              <input
                type="checkbox"
                checked={activate}
                onChange={(e) => setActivate(e.target.checked)}
              />
              Activate immediately
            </label>
            <button type="submit" className="sysnotif-btn sysnotif-btn--primary" disabled={submitting || !message.trim()}>
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </section>

      {/* Notification List */}
      <section className="sysnotif-list-section">
        <h2>All Notifications</h2>
        {loading ? (
          <p className="sysnotif-empty">Loading...</p>
        ) : notifications.length === 0 ? (
          <p className="sysnotif-empty">No notifications yet.</p>
        ) : (
          <div className="sysnotif-list">
            {notifications.map((n) => (
              <div key={n.id} className={`sysnotif-item sysnotif-item--${n.type} ${n.is_active ? 'sysnotif-item--active' : ''}`}>
                <div className="sysnotif-item-header">
                  <span className={`sysnotif-badge sysnotif-badge--${n.type}`}>{n.type}</span>
                  {n.is_active && <span className="sysnotif-active-pill">● ACTIVE</span>}
                  <span className="sysnotif-date">{formatDate(n.created_at)}</span>
                </div>
                <p className="sysnotif-message">{n.message}</p>
                <div className="sysnotif-actions">
                  {!n.is_active && (
                    <button className="sysnotif-btn sysnotif-btn--activate" onClick={() => handleActivate(n.id)}>
                      Activate
                    </button>
                  )}
                  {n.is_active && (
                    <button className="sysnotif-btn sysnotif-btn--deactivate" onClick={() => handleDeactivate(n.id)}>
                      Deactivate
                    </button>
                  )}
                  <button className="sysnotif-btn sysnotif-btn--danger" onClick={() => handleDelete(n.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SystemNotificationsPage;
